import { useEffect, useState } from 'react';
import Drawer from '../common/Drawer';
import ShellLink from '../common/ShellLink';
import api from '../../services/api';
import type { LinkedRoom } from '../../types';
import DocumentsPanel from '../DocumentsPanel';
import KnowledgeBriefPanel from '../KnowledgeBriefPanel';
import KnowledgeSuggestLauncher from '../KnowledgeSuggestLauncher';
import type { KnowledgeAgentScope } from '../../types';

/**
 * What this node knows, reachable from the subject it belongs to.
 *
 * Every panel in here already existed and could only be opened by typing a
 * legacy settings URL — `/projects|/categories|/topics/:id/settings/knowledge`.
 * Nothing in the Subject workspace linked to any of them, so the feature was
 * effectively unreachable for anyone who did not already know the route.
 *
 * TWO TABS, NOT THREE PANELS STACKED. Rules and reference are the same
 * question at two lengths and belong together; documents are files you upload
 * and have their own management (delete, processing state), so stacking them
 * under a long proposal list buried them.
 *
 * The scope words are the app's, not this component's: `project | category |
 * topic`, where "category" is a node with no parent. That vocabulary predates
 * the tree and retires with the settings screens — this passes it through
 * rather than inventing a fourth name for the same thing.
 */

type Tab = 'knowledge' | 'documents';

interface Props {
  onClose: () => void;
  scope: KnowledgeAgentScope;
  scopeId: number;
  scopeName: string;
  /** Where this node sits, so the drawer says which node it is editing when
   *  several share a name — "Guides" under two different subjects is common. */
  scopePath?: string;
  /** The subject, so the drawer can name the OTHER things that feed the writer.
   *  What is typed here is only part of what reaches a build, and a panel
   *  headed "what the writing agent should know" that lists only its own two
   *  tabs quietly implies it is the whole of it. */
  projectId?: number;
  /** Which tab to open on. Set when something deep-linked here to show a
   *  specific thing — "see the documents feeding this build" lands on the
   *  Documents tab or it has not answered the question. */
  initialTab?: Tab;
}

export default function KnowledgeDrawer({
  onClose,
  scope,
  scopeId,
  scopeName,
  scopePath,
  projectId,
  initialTab,
}: Props) {
  const [tab, setTab] = useState<Tab>(initialTab ?? 'knowledge');
  const [room, setRoom] = useState<LinkedRoom | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let live = true;
    api
      .getRoomForSubject(projectId)
      // Null is the ordinary answer for most subjects, and a failure here must
      // not take the drawer down with it — this is context, not a control.
      .then(r => { if (live) setRoom(r); })
      .catch(() => {});
    return () => { live = false; };
  }, [projectId]);

  return (
    <Drawer
      onClose={onClose}
      side="right"
      width="w-[46rem]"
      eyebrow={scopePath || 'Subject'}
      title={`Knowledge · ${scopeName}`}
    >
      <div className="mb-4">
        {/* Says what the thing is FOR before showing the controls. Four ways in
            — rules, reference, documents, and a linked room later — is a lot to
            infer from a list of textareas. */}
        <p className="text-sm text-gray-600 dark:text-gray-300">
          What the writing agent should know about{' '}
          <span className="font-medium">{scopeName}</span>. Rules are short and
          go into every prompt; reference and documents are longer and are looked
          up when they are relevant.
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Nodes inherit from everything above them, so a rule written here also
          applies to every node underneath.
        </p>
        {/* THE REST OF WHAT REACHES A BUILD. Rules and documents are what you
            type; a linked room and the trend a page came from are read live at
            build time, and neither is visible from this drawer. Naming them
            here stops the two tabs reading as the complete list. */}
        <p className="mt-2 rounded bg-gray-50 px-2 py-1.5 text-xs text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
          Also reaching the writer, without being typed here:{' '}
          {room ? (
            /* ShellLink: the room lives in the Boardroom product, on another
               host, so a router link would render it inside this shell. */
            <ShellLink
              to={`/boardroom/${room.boardroom_id}`}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              the linked room
            </ShellLink>
          ) : (
            <span>no room is linked</span>
          )}
          , and the trending story a page was created from. Both are read live
          when a build runs - each page builder lists what it will use.
        </p>
      </div>

      <div className="mb-4 flex gap-4 border-b border-gray-200 dark:border-gray-700 text-sm font-medium">
        {(
          [
            ['knowledge', 'Rules & reference'],
            ['documents', 'Documents'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`pb-2 ${
              tab === key
                ? 'text-gray-900 dark:text-gray-100 border-b-2 border-blue-500'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'knowledge' ? (
        <>
          <KnowledgeSuggestLauncher
            scope={scope}
            scopeId={scopeId}
            scopeName={scopeName}
          />
          <KnowledgeBriefPanel
            scope={scope}
            scopeId={scopeId}
            scopeName={scopeName}
          />
        </>
      ) : (
        // `DocumentsPanel` takes ONE of project_id / topic_id — the scope it is
        // given, not a chain. Retrieval walks the chain at build time; listing
        // deliberately shows only what is attached HERE, so "what did I put on
        // this node" has an answer.
        <DocumentsPanel
          {...(scope === 'project'
            ? { project_id: scopeId }
            : { topic_id: scopeId })}
          label={`Documents on ${scopeName}`}
        />
      )}
    </Drawer>
  );
}
